import { Component, computed, EventEmitter, Input, Output } from '@angular/core';
import { v3ComponentContext } from '../services/v3-component-context';
import { findV3AnalysisSignal } from '../services/v3-guest-mode';
import { CompetitionPeer } from '../competitor-analysis/competitor-analysis';
import { V3CompetitorTable } from '../v3-competitor-table/v3-competitor-table';
import { V3CompetitorHighlights } from '../v3-competitor-highlights/v3-competitor-highlights';

const FULL_LIMIT = 20;

// V3's Wettbewerb detail sub-page — same content as the overview card, without its chrome. See CLAUDE.md.
@Component({
  selector: 'app-v3-competitor-analysis',
  imports: [V3CompetitorTable, V3CompetitorHighlights],
  standalone: true,
  templateUrl: './v3-competitor-analysis.html',
  styleUrl: './v3-competitor-analysis.scss',
})
export class V3CompetitorAnalysis {
  private readonly ctx = v3ComponentContext();

  /** Live EUR price per ISIN, for V3CompetitorTable. */
  @Input() livePrices: Record<string, number> | null = null;
  @Output() navigateToInstrument = new EventEmitter<string>();

  readonly limit = FULL_LIMIT;

  readonly isZeroDesktop = this.ctx.layout.isZeroDesktop;
  readonly isFinnetDesktop = this.ctx.layout.isFinnetDesktop;
  readonly isFinnet = this.ctx.layout.isFinnet;

  private readonly isGuestMode = this.ctx.isGuestMode;

  private readonly competitionAnalysis = findV3AnalysisSignal(
    this.ctx.assetsStore.v2Data,
    'competition',
    this.isGuestMode,
  );

  readonly advantage = computed(
    () => (this.competitionAnalysis()?.content?.['advantage'] as string | null) ?? null,
  );
  readonly risk = computed(
    () => (this.competitionAnalysis()?.content?.['risk'] as string | null) ?? null,
  );

  // Restricted (regwall) entries filtered out; subject (highlighted row) is separate below.
  readonly peers = computed<CompetitionPeer[]>(() => {
    const raw = this.competitionAnalysis()?.content?.['peers'];
    if (!Array.isArray(raw)) return [];
    return (raw as (CompetitionPeer & { restricted?: boolean })[]).filter((p) => !p.restricted);
  });

  // isin/name/logo come from the header's own asset data, not content.subject (can be stale,
  // missing, or — for guests — the mock's placeholder values).
  readonly subject = computed<CompetitionPeer | null>(() => {
    const raw = this.competitionAnalysis()?.content?.['subject'] as
      | (CompetitionPeer & { restricted?: boolean })
      | undefined;
    if (!raw || raw.restricted) return null;
    const asset = this.ctx.assetsStore.v2Data();
    return {
      ...raw,
      isin: asset?.isin ?? raw.isin,
      name: asset?.name ?? raw.name,
      logo_url: asset?.iconUrl ?? raw.logo_url,
    };
  });
}
